import { QrCodeIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import { AuthShell } from '../components/AuthShell'
import {
  PreferenceCategory,
  PreferenceInfo,
  PreferenceInput,
  PreferenceLink,
  PreferenceScreen,
  PreferenceSubmit,
} from '../components/Preference'
import { ensureAnonymous, useMe } from '../lib/auth-client'

/**
 * Create-only onboarding for signed-in users without a tenant.
 * Join is handled via /invite/:code (requires real identity).
 */
export default function Onboarding() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [name, setName] = useState(params.get('name') || '')
  const [busy, setBusy] = useState(false)
  const { data: me, isLoading, mutate } = useMe()
  const [bootstrapping, setBootstrapping] = useState(false)

  useEffect(() => {
    if (!me || bootstrapping) return
    if (me.user?.tenantId != null) {
      navigate('/planning', { replace: true })
      return
    }
    if (!me.user) {
      setBootstrapping(true)
      void ensureAnonymous().then(async ({ error: anonErr }) => {
        if (anonErr) {
          navigate('/', { replace: true })
          return
        }
        await mutate()
        setBootstrapping(false)
      })
    }
  }, [me, navigate, mutate, bootstrapping])

  const ready = me?.user != null && me.user.tenantId == null && !bootstrapping

  async function createHousehold() {
    setBusy(true)
    try {
      const res = await fetch('/api/tenants', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim() || 'Mijn huishouden' }),
      })
      const data = await res.json()
      if (!res.ok) {
        toast.error(data.message || 'Mislukt')
        return
      }
      navigate('/planning', { replace: true })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  if (isLoading || !ready) {
    return (
      <AuthShell>
        <PreferenceInfo title="Laden…" />
      </AuthShell>
    )
  }

  return (
    <AuthShell>
      <PreferenceScreen title="Jouw huishouden">
        <PreferenceCategory filled>
          <PreferenceInput
            title="Naam huishouden"
            value={name}
            onChange={setName}
            placeholder="Wilgenstraat"
            autoFocus
          />
          <PreferenceSubmit
            title={busy ? 'Bezig…' : 'Doorgaan'}
            onPress={createHousehold}
            disabled={busy}
          />
        </PreferenceCategory>

        <PreferenceCategory filled title="Zit jouw huishouden al in de app?">
          <PreferenceLink
            icon={<QrCodeIcon className="h-6 w-6" />}
            title="Scan uitnodigings-QR"
            summary="Deelnemen aan een bestaand huishouden"
            href="/join"
          />
        </PreferenceCategory>
      </PreferenceScreen>
    </AuthShell>
  )
}
